/**
 * Shared ledger-margin atoms for agent chat rails (ChatRail + live AgentChat).
 * The left border is the spine; mono timestamps ride the masthead line.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { scrubVendorTerms } from "@/lib/vendorTerms";
import type { MessageThinking } from "@/types/chat.type";
import { ChatTextWithUuidPills } from "./chatUuidPills";
import { getToolIcon } from "./toolCallDisplay";

const TOOL_PAYLOAD_CAP = 20_000;

export function formatToolPayload(value: unknown): string {
  if (value === null || value === undefined) return "";
  let text: string;
  if (typeof value === "string") text = value;
  else {
    try {
      text = JSON.stringify(value, null, 2);
    } catch {
      text = String(value);
    }
  }
  const cleaned = scrubVendorTerms(text);
  if (cleaned.length <= TOOL_PAYLOAD_CAP) return cleaned;
  return `${cleaned.slice(0, TOOL_PAYLOAD_CAP)}\n… truncated`;
}

export function formatRailTime(iso: string): { at: string; iso: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { at: "—", iso };
  return {
    at: d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    iso,
  };
}

export function RailTimeStamp({
  at,
  iso,
  float = false,
}: {
  at: string;
  iso?: string;
  float?: boolean;
}) {
  return (
    <time
      dateTime={iso ?? at}
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10.5,
        color: "var(--co-faint)",
        letterSpacing: ".02em",
        lineHeight: 1.5,
        whiteSpace: "nowrap",
        ...(float ? { float: "right", marginLeft: 8 } : { flex: "none" }),
      }}
    >
      {at}
    </time>
  );
}

export function RailRow({
  at,
  iso,
  tag,
  tagColor = "var(--co-faint)",
  tagVariant = "ledger",
  authorHue,
  authorSelf = false,
  lit = false,
  band = false,
  tight = false,
  floatTime = true,
  className,
  children,
}: {
  at: string;
  iso?: string;
  tag?: string;
  tagColor?: string;
  /** "author" is a person's name on a shared-thread message. */
  tagVariant?: "ledger" | "author";
  /** Stable color slot for someone else's name. */
  authorHue?: number;
  /** The viewer's own "You", in the platform accent. */
  authorSelf?: boolean;
  lit?: boolean;
  band?: boolean;
  /** Tighter block padding — tool clusters sit closer than a prose turn. */
  tight?: boolean;
  floatTime?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={["co-rail-row", className].filter(Boolean).join(" ")}
      data-band={band || undefined}
      data-author={authorSelf ? "self" : undefined}
      data-author-hue={authorHue}
      style={{
        ...(band
          ? {
              background: "var(--co-entry)",
              margin: "0 -14px 0 -6px",
              padding: "0 14px 0 6px",
            }
          : null),
      }}
    >
      <div
        className="co-rail-row-body"
        style={{
          minWidth: 0,
          borderLeft: `1px solid ${lit ? "var(--co-accent)" : "var(--co-line)"}`,
          padding: band
            ? "9px 2px 9px 14px"
            : tight
              ? "1px 2px 8px 14px"
              : "2px 2px 18px 14px",
          transition: "border-color .3s var(--ease-out-expo)",
        }}
      >
        {floatTime || tag ? (
          <div style={{ display: "flow-root" }}>
            {floatTime ? <RailTimeStamp at={at} iso={iso} float /> : null}
            {tag ? (
          <div
            className="co-rail-row-tag"
            style={
              tagVariant === "author"
                ? {
                    maxWidth: "calc(100% - 52px)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    marginBottom: 4,
                    fontFamily: "var(--font-body)",
                    fontWeight: 700,
                    fontSize: 11,
                    letterSpacing: "0.01em",
                    lineHeight: 1.3,
                    textTransform: "none",
                    color: tagColor,
                    whiteSpace: "nowrap",
                  }
                : {
                    maxWidth: "calc(100% - 44px)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    marginBottom: 5,
                    fontFamily: "var(--font-section)",
                    fontWeight: 700,
                    fontSize: 8.5,
                    letterSpacing: ".12em",
                    textTransform: "uppercase",
                    color: tagColor,
                    whiteSpace: "nowrap",
                  }
            }
          >
            {tag}
          </div>
            ) : null}
          </div>
        ) : null}
        {children}
      </div>
    </div>
  );
}

export function RailUserLine({
  at,
  iso,
  text,
  tag = "YOU",
  authorTag = false,
  named = false,
  authorHue,
  self = false,
  badge,
  seenAnchor,
}: {
  at: string;
  iso?: string;
  text: string;
  /**
   * Who wrote it. "YOU" on a private thread. A shared thread uses the
   * person's name, or "You" for the viewer.
   */
  tag?: string;
  /** The tag is a person's name, not the ledger "YOU" label. */
  authorTag?: boolean;
  /** Show the name above the message. A repeated sender in a row leaves it off. */
  named?: boolean;
  /** Color slot for someone else's name. */
  authorHue?: number;
  /** The viewer's own message. "You" uses the platform accent. */
  self?: boolean;
  /** A mark under the message, at its bottom left (a shared thread's "seen"). */
  badge?: ReactNode;
  /**
   * Empty slot where a shared-thread "seen" mark sits. One floating badge
   * measures these and slides between them.
   */
  seenAnchor?: string;
}) {
  return (
    <RailRow
      at={at}
      iso={iso}
      tag={tag}
      tagColor={self ? "var(--co-accent-ink)" : authorTag ? "var(--ws-name-0)" : "var(--co-text2)"}
      tagVariant={authorTag ? "author" : "ledger"}
      authorHue={named && !self ? authorHue : undefined}
      authorSelf={named && self}
      band
      className={named ? "co-rail-row--user co-rail-row--named" : "co-rail-row--user"}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
        <span
          aria-hidden="true"
          style={{
            flex: "none",
            fontFamily: "var(--font-mono)",
            fontSize: 13,
            lineHeight: 1.5,
            color: "var(--co-muted)",
          }}
        >
          ›
        </span>
        <span
          style={{
            minWidth: 0,
            fontFamily: "var(--font-body)",
            fontSize: 12,
            lineHeight: 1.5,
            fontWeight: 500,
            color: "var(--co-text)",
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
          }}
        >
          <ChatTextWithUuidPills text={text} />
        </span>
      </div>
      {seenAnchor ? (
        <span
          className="co-rail-seen-anchor"
          data-seen-anchor={seenAnchor}
          aria-hidden="true"
        />
      ) : null}
      {badge ? (
        <div
          className="co-rail-seen"
          style={{
            position: "absolute",
            zIndex: 1,
            bottom: -10,
            insetInlineStart: -10,
          }}
        >
          {badge}
        </div>
      ) : null}
    </RailRow>
  );
}

const SEEN_MOVE = "transform 460ms cubic-bezier(0.16, 1, 0.3, 1)";

function seenAnchorOffset(anchor: HTMLElement, root: HTMLElement) {
  const rootBox = root.getBoundingClientRect();
  const box = anchor.getBoundingClientRect();
  return {
    x: box.left - rootBox.left - root.clientLeft + root.scrollLeft,
    y: box.top - rootBox.top - root.clientTop + root.scrollTop,
  };
}

/**
 * One "seen" mark for the transcript. It stays mounted and slides to the
 * newest message the assistant has read, instead of unmounting and popping
 * in on that row.
 */
export function RailSeenBadge({
  messageId,
  children,
}: {
  messageId: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const prevId = useRef<string | null>(null);
  const moving = useRef(false);
  const currentId = useRef(messageId);

  useLayoutEffect(() => {
    currentId.current = messageId;
  });

  useLayoutEffect(() => {
    const el = ref.current;
    const root = el?.closest("[role='log']");
    if (!el || !(root instanceof HTMLElement)) return;

    const anchor = root.querySelector<HTMLElement>(
      `[data-seen-anchor="${CSS.escape(messageId)}"]`,
    );
    if (!anchor) {
      el.style.visibility = "hidden";
      return;
    }

    const next = seenAnchorOffset(anchor, root);
    const fromId = prevId.current;
    prevId.current = messageId;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.style.visibility = "visible";

    if (fromId && fromId !== messageId && !reduce && !moving.current) {
      const from = root.querySelector<HTMLElement>(
        `[data-seen-anchor="${CSS.escape(fromId)}"]`,
      );
      const start = from ? seenAnchorOffset(from, root) : null;
      const distance = start
        ? Math.hypot(next.x - start.x, next.y - start.y)
        : 0;
      if (start && distance >= 2) {
        el.style.transition = "none";
        el.style.transform = `translate3d(${start.x}px, ${start.y}px, 0)`;
        void el.offsetWidth;
        moving.current = true;
        el.dataset.moving = "true";
        el.style.transition = SEEN_MOVE;
        el.style.transform = `translate3d(${next.x}px, ${next.y}px, 0)`;
        return;
      }
    }

    if (moving.current) {
      el.style.transform = `translate3d(${next.x}px, ${next.y}px, 0)`;
      return;
    }

    el.dataset.moving = "false";
    el.style.transition = "none";
    el.style.transform = `translate3d(${next.x}px, ${next.y}px, 0)`;
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const done = (event: TransitionEvent) => {
      if (event.propertyName !== "transform" || event.target !== el) return;
      moving.current = false;
      el.dataset.moving = "false";
      el.style.transition = "none";
    };
    // A cancelled slide never fires transitionend; without this the mark
    // would stay "moving" and never snap again.
    el.addEventListener("transitionend", done);
    el.addEventListener("transitioncancel", done);

    // The anchor moves without this component re-rendering: the panel
    // resizes, or a streaming reply above it grows. Follow it there.
    const root = el.closest("[role=log]");
    if (!(root instanceof HTMLElement)) {
      return () => {
        el.removeEventListener("transitionend", done);
        el.removeEventListener("transitioncancel", done);
      };
    }
    const snap = () => {
      if (moving.current) return;
      const anchor = root.querySelector<HTMLElement>(
        `[data-seen-anchor="${CSS.escape(currentId.current)}"]`,
      );
      if (!anchor) return;
      const next = seenAnchorOffset(anchor, root);
      el.style.transform = `translate3d(${next.x}px, ${next.y}px, 0)`;
    };
    const resize = new ResizeObserver(snap);
    const observeRows = () => {
      resize.observe(root);
      for (const child of Array.from(root.children)) {
        if (child !== el) resize.observe(child);
      }
    };
    observeRows();
    const rows = new MutationObserver(() => {
      observeRows();
      snap();
    });
    rows.observe(root, { childList: true });
    return () => {
      el.removeEventListener("transitionend", done);
      el.removeEventListener("transitioncancel", done);
      resize.disconnect();
      rows.disconnect();
    };
  }, []);

  return (
    <div ref={ref} className="co-rail-seen-layer" style={{ visibility: "hidden" }}>
      {children}
    </div>
  );
}

/**
 * One quiet line under an interactive card: who may answer it, or who did.
 * Shared workshop threads only; everywhere else the card is the viewer's own.
 */
export function RailCardFootnote({ children }: { children: ReactNode }) {
  return (
    <p
      style={{
        marginTop: 10,
        fontFamily: "var(--font-body)",
        fontSize: 11.5,
        lineHeight: 1.45,
        color: "var(--co-muted)",
      }}
    >
      {children}
    </p>
  );
}

export function RailThinkBlock({
  at,
  iso,
  thinking,
  isLive = false,
}: {
  at: string;
  iso?: string;
  thinking: MessageThinking;
  isLive?: boolean;
}) {
  const forceOpen = isLive;
  const [open, setOpen] = useState(false);
  const isOpen = open || forceOpen;
  const bodyRef = useRef<HTMLDivElement>(null);
  const sec =
    thinking.durationMs > 0
      ? `${(thinking.durationMs / 1000).toFixed(1)}s`
      : null;
  const steps = scrubVendorTerms(thinking.text)
    .split(/\n{2,}/)
    .map((s) => s.trim())
    .filter(Boolean);

  useEffect(() => {
    if (!isOpen) return;
    const el = bodyRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [thinking.text, isLive, isOpen]);

  return (
    <RailRow at={at} iso={iso}>
      <button
        type="button"
        className="co-think-head"
        aria-expanded={isOpen}
        onClick={() => !forceOpen && setOpen((o) => !o)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          background: "none",
          border: "none",
          padding: 0,
          cursor: forceOpen ? "default" : "pointer",
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          letterSpacing: ".06em",
          color: "var(--co-faint)",
        }}
      >
        <span aria-hidden="true">
          {forceOpen ? "" : isOpen ? "▾" : "▸"}
        </span>
        <span>
          THINKING
          {forceOpen ? (
            <span className="co-livedot"> ▌</span>
          ) : (
            <>
              {sec ? ` · ${sec}` : ""}
              {steps.length > 0 ? ` · ${steps.length} step${steps.length === 1 ? "" : "s"}` : ""}
            </>
          )}
        </span>
      </button>
      <div className="co-think-fold" data-open={isOpen}>
        <div>
          <div
            ref={bodyRef}
            className="co-think-body"
            style={{
              marginTop: 9,
              marginLeft: 2,
              paddingLeft: 14,
              borderLeft: "1px solid var(--co-line2)",
              display: "flex",
              flexDirection: "column",
              gap: 7,
            }}
          >
            {(steps.length > 0 ? steps : [thinking.text]).map((s, i) => (
              <div key={i} style={{ display: "flex" }}>
                <span
                  style={{
                    flex: "none",
                    width: 22,
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    color: "var(--co-faint)",
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span
                  style={{
                    minWidth: 0,
                    fontFamily: "var(--font-body)",
                    fontSize: 12,
                    lineHeight: 1.65,
                    color: "var(--co-muted)",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {s}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </RailRow>
  );
}

export function RailActLine({
  tool,
  toolName,
  label,
  status,
  count = 1,
  trailing,
  showProgress = true,
  inspect,
}: {
  tool: string;
  /** Real tool id, used to pick the glyph that replaces the old status square. */
  toolName?: string;
  label: string;
  status: "run" | "ok" | "err";
  count?: number;
  trailing?: ReactNode;
  /** Indeterminate fill under the line. Off for waits (no meaningful % progress). */
  showProgress?: boolean;
  /** Dev-only: tool input/result already on the event. Click the row to expand. */
  inspect?: { input?: unknown; result?: unknown } | null;
}) {
  const [progress, setProgress] = useState(0.08);
  const [open, setOpen] = useState(false);
  const progressVisible = showProgress && status === "run";
  const { icon: Icon } = getToolIcon(toolName ?? "");
  const iconColor =
    status === "run"
      ? "var(--co-accent-ink)"
      : status === "err"
        ? "var(--co-alert)"
        : "var(--co-live)";
  const formattedInput = inspect ? formatToolPayload(inspect.input) : "";
  const formattedResult = inspect ? formatToolPayload(inspect.result) : "";
  const canInspect =
    Boolean(inspect) &&
    status !== "run" &&
    (formattedInput.length > 0 || formattedResult.length > 0);

  useEffect(() => {
    if (!progressVisible) return;
    let disposed = false;
    queueMicrotask(() => {
      if (!disposed) setProgress(0.08);
    });
    const started = performance.now();
    // Ease toward ~92% over ~18s, then creep — never finish until status flips.
    const id = window.setInterval(() => {
      const t = (performance.now() - started) / 18_000;
      const eased = 1 - Math.exp(-2.2 * Math.min(t, 1));
      const creep = t > 1 ? Math.min(0.06, (t - 1) * 0.02) : 0;
      setProgress(Math.min(0.94, 0.08 + eased * 0.8 + creep));
    }, 120);
    return () => {
      disposed = true;
      window.clearInterval(id);
    };
  }, [progressVisible]);

  const row = (
    <>
      <span
        className={status === "run" ? "co-livedot" : undefined}
        style={{
          flex: "none",
          display: "grid",
          placeItems: "center",
          color: iconColor,
        }}
      >
        <Icon style={{ width: 11, height: 11 }} strokeWidth={1.75} aria-hidden />
      </span>
      <span
        style={{
          flex: "none",
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          letterSpacing: ".06em",
          color: status === "run" ? "var(--co-accent-ink)" : "var(--co-text)",
        }}
      >
        {tool}
      </span>
      <span
        style={{
          flex: "0 1 auto",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontFamily: "var(--font-body)",
          fontSize: 11,
          color: "var(--co-muted)",
        }}
      >
        {label}
      </span>
      {count > 1 ? (
        <span
          style={{
            flex: "none",
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            letterSpacing: ".04em",
            color: "var(--co-faint)",
          }}
        >
          ×{count}
        </span>
      ) : null}
      {canInspect ? (
        <ChevronDown
          aria-hidden
          strokeWidth={1.75}
          style={{
            flex: "none",
            width: 10,
            height: 10,
            color: "var(--co-faint)",
            transform: open ? "rotate(180deg)" : undefined,
            transition: "transform 120ms ease",
          }}
        />
      ) : null}
      {trailing ? <span style={{ flex: "none", marginLeft: "auto" }}>{trailing}</span> : null}
    </>
  );

  return (
    <div>
      {canInspect ? (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            width: "100%",
            margin: 0,
            padding: 0,
            border: "none",
            background: "none",
            color: "inherit",
            cursor: "pointer",
            textAlign: "start",
          }}
        >
          {row}
        </button>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>{row}</div>
      )}
      {progressVisible ? (
        <div style={{ height: 2, background: "var(--co-line2)", marginTop: 3 }}>
          <div
            className="co-act-progress-fill"
            style={{
              height: "100%",
              transform: `scaleX(${Math.min(Math.max(progress, 0), 1)})`,
              transformOrigin: "left",
              background: "var(--co-accent)",
            }}
          />
        </div>
      ) : null}
      {open && canInspect ? (
        <pre className="co-dev-tool-payload">
          {formattedInput ? `IN\n${formattedInput}${formattedResult ? "\n\n" : ""}` : null}
          {formattedResult ? `OUT\n${formattedResult}` : "OUT\n(empty)"}
        </pre>
      ) : null}
    </div>
  );
}
